import { WebsiteModel } from './../../../../portal.model';
import {
  Component,
  Input,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  UntypedFormGroup,
  Validators,
  UntypedFormBuilder,
} from '@angular/forms';
import { Router } from '@angular/router';
import {
  LearmoModalRef,
  LearmoModalService,
} from '../../../../../../../shared/services/learmo-modal.service';
import { ToastrService } from 'ngx-toastr';

import { lastValueFrom } from 'rxjs';
import { ApiService } from '../../../../../../../shared/services/lsg.api.service';

import { SharedPortalViewService } from '../../../shared-portal-view.service';
import { Store } from '@ngrx/store';
import { addNewWebsitePage } from 'src/app/shared/store/_features/website/website.actions';
import { PageItem } from 'src/app/models/portal';

export enum InvitationCurrentStepEnum {
  input = 'input',
  result = 'result',
}
@Component({
  selector: 'app-create-page-popup',
  templateUrl: './create-page-popup.component.html',
  styleUrls: ['./create-page-popup.component.css'],
})
export class CreatePagePopupComponent implements OnInit {
  createPageForm: UntypedFormGroup;

  processing: 'none' | 'loading' = 'none';
  slugError: string = '';
  @ViewChild('modalContent') modalContent: TemplateRef<any>;
  @ViewChild('modalFooter') modalFooter: TemplateRef<any>;
  @Input() modalRef: LearmoModalRef;
  @Input() website: WebsiteModel;
  constructor(
    private modalService: LearmoModalService,
    private route: Router,
    private api: ApiService,
    private fb: UntypedFormBuilder,
    private toastr: ToastrService,
    public sharedPortalViewService: SharedPortalViewService,
    private store: Store,
  ) {}

  async ngOnInit() {
    this.buildForm();
  }

  startProcessing() {
    this.processing = 'loading';
  }

  stopProcessing() {
    this.processing = 'none';
  }

  async createNewPage() {
    this.createPageForm.markAllAsTouched();
    const newPageName = this.createPageForm.controls.name.value;
    const newPageSlug = this.createPageForm.controls.slug.value;

    if (this.createPageForm.valid) {
      // Check for slug uniqueness before submitting?
      // Requirement says "Slug Uniqueness Validation ... On save/creation".
      // I'll assume backend handles it or I should check it.
      // The implementation plan said "Add real-time/on-submit validation".
      // I'll check it here using checksSlugUnique I added to API.

      this.startProcessing();

      try {
        const checkRes: any = await lastValueFrom(
          this.api.checkSlugUnique(newPageSlug),
        );
        console.log(checkRes);
        if (checkRes.exists) {
          // Assuming success means unique, or handling error
          this.toastr.error(
            'This URL slug is already taken. Please choose another.',
          );
          this.stopProcessing();
          return;
        }

        const newPage$ = this.api.createNewPage(
          newPageName,
          newPageSlug,
          this.addNewPageErrorHandle,
        );
        const newPage: PageItem = await lastValueFrom(newPage$);
        this.store.dispatch(addNewWebsitePage({ page: newPage }));
        this.onCreateDone(newPage);
      } catch (e: any) {
        this.addNewPageErrorHandle(e);
        this.stopProcessing();
      }
    }
  }

  async callScreenShot(url: string, fullPage: boolean, https: boolean = true) {
    if (!!https) {
      url = 'https://' + url;
    }
    const img$ = this.api.screenshot(url, fullPage);
    const img = await lastValueFrom(img$);
    return img.img;
  }

  addNewPageErrorHandle = (error: any) => {
    this.toastr.error(error?.message);
    this.closeModal();
  };

  onCreateDone(page?: PageItem) {
    this.closeModal();

    this.toastr.success('Done!', 'Page Created Successfully', {
      closeButton: false,
      positionClass: 'toast-bottom-right',
    });

    if (page) {
      // Redirect to Visual Builder
      // URL format from page-list-item: http://${this.page.url}/?builder=learmo&id=${this.page?.id}
      // Or internal router link? The item uses href for builder.
      // I'll use window.open or window.location.href.
      // "opens Visual Builder automatically" -> usually means navigation.

      // Since it's an external URL (http://...), I should use window.location.href or window.open.
      // But page.url might be relative or absolute? In page-list-item it prepends 'http://'.
      // I'll assume it's domain/slug.

      const builderUrl = `http://${page.url}/?builder=learmo&id=${page.id}`;
      window.open(builderUrl, '_blank'); // Open in new tab or same? "opens Visual Builder".
      // Usually builders open in the same tab or new info.
      // I'll defer to same tab if possible or new tab.
      // "Post-Creation Behavior: ... opens Visual Builder automatically."
      // I'll open in new tab to match "Visual Builder" generic behavior often seen.
    }
  }

  resetModal() {
    this.stopProcessing();
    this.createPageForm.reset();
  }
  closeModal() {
    this.modalRef.dismiss();
    this.resetModal();
  }

  buildForm() {
    this.createPageForm = this.fb.group({
      name: ['', Validators.required],
      slug: [
        '',
        [Validators.required, Validators.pattern(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)],
      ],
    });

    this.createPageForm.get('name').valueChanges.subscribe((value) => {
      if (value && !this.createPageForm.get('slug').dirty) {
        const slug = this.normalizeSlug(value);
        this.createPageForm.get('slug').setValue(slug);
      }
    });

    this.createPageForm.get('slug').valueChanges.subscribe((value) => {
      if (value) {
        const normalized = this.normalizeSlug(value);
        if (value !== normalized) {
          this.createPageForm
            .get('slug')
            .setValue(normalized, { emitEvent: false });
        }
      }
    });

    this.createPageForm.get('slug').valueChanges.subscribe(() => {
      this.slugError = ''; // Clear error on edit
    });
  }

  async checkSlugAvailability() {
    const slug = this.createPageForm.get('slug')?.value;
    if (!slug) return;

    // Only check if valid format
    if (this.createPageForm.get('slug')?.invalid) return;

    try {
      const checkRes: any = await lastValueFrom(this.api.checkSlugUnique(slug));
      if (checkRes.exists) {
        // API returns { exists: boolean }
        this.slugError = 'This URL slug has been taken.';
        this.createPageForm.get('slug').setErrors({ notUnique: true });
      } else {
        this.slugError = '';
        // Remove notUnique error if present, keep others
        const errors = this.createPageForm.get('slug').errors;
        if (errors) {
          delete errors['notUnique'];
          if (Object.keys(errors).length === 0) {
            this.createPageForm.get('slug').setErrors(null);
          } else {
            this.createPageForm.get('slug').setErrors(errors);
          }
        }
      }
    } catch (e) {
      console.error('Failed to check slug', e);
    }
  }

  normalizeSlug(value: string): string {
    return value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  // Modal Open
  async open(content?) {
    this.onOpenModal();
    this.modalRef = this.modalService.open(this.modalContent, {
      title: 'Create new portal page',
      footer: this.modalFooter,
      premium: true,
      loading: this.processing === 'loading',
    });
  }

  async onOpenModal() {
    // this.resetForm();
    // this.productObject.type_id = 1;
  }
}
